import { useState, useEffect } from 'react'
import Login from './pages/Login';
import Home from './pages/Home';
import ClaimDevice from './components/ClaimDevice';
import NoAccess from './components/NoAccess';
import CreateAccount from './components/CreateAccount';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import InstallApp from './components/InstallApp';
import Copyrights from "./components/Footer";

import { getData, postJson } from './global/func';
import './styles/app.css';

const STORAGE_USER_KEY = 'app_user';
const STORAGE_DEVICES_KEY = 'app_devices';

const EMPTY_USER = { id: '0', name: '', auth_key: '' };

// The reset link lands here carrying its token. There is no router in this app,
// so the query string is read once at startup; the token is then dropped from
// the address bar, because a reset link left in history or copied out of it is
// a live credential until it is spent.
const takeQueryToken = (name) => {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get(name);
    if (!token) return '';

    // Only this parameter is removed, not the whole query string. Both tokens
    // are read at startup, so clearing everything would mean whichever ran
    // first silently threw the other away.
    params.delete(name);
    const rest = params.toString();
    window.history.replaceState({}, document.title,
      window.location.pathname + (rest ? `?${rest}` : ''));
    return token;
  } catch { return ''; }
};

const takeResetToken = () => takeQueryToken('reset');

// An invitation to somebody else's box. Read the same way and for the same
// reason: it is a credential, and one left in the address bar is one left in
// history and in anything that syncs it.
const takeInviteToken = () => takeQueryToken('invite');

const getSavedUser = () => {
  try {
    const saved = localStorage.getItem(STORAGE_USER_KEY);
    return saved ? JSON.parse(saved) : { ...EMPTY_USER };
  } catch { return { ...EMPTY_USER }; }
};

// The devices endpoint returns an array. It used to be stored alongside an
// object-shaped default, and the render check tested `userDevices.id`, which is
// undefined for any array -- so a signed-in customer with no devices fell
// through to Settings, which indexes Devices[0] and crashes. That is exactly the
// state someone is in the moment they unbox their first Prayer Box.
const getSavedDevices = () => {
  try {
    const saved = localStorage.getItem(STORAGE_DEVICES_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const App = () => {

  // State — initialized from localStorage so it survives page refresh
  const [userInfo, setUserInfo] = useState(getSavedUser);
  const [userDevices, setUserDevices] = useState(getSavedDevices);
  const [addingDevice, setAddingDevice] = useState(false);
  const [resetToken, setResetToken] = useState(takeResetToken);
  const [inviteToken, setInviteToken] = useState(takeInviteToken);
  // What happened to an invitation spent while already signed in. The one place
  // the outcome can be said, since that path shows no screen of its own.
  const [inviteNotice, setInviteNotice] = useState(null);
  // Invited, but the address already has an account. The token stays held while
  // they sign in, because the effect above is what spends it and it needs them
  // signed in first. Without this flag the invite screen would render forever.
  const [inviteSignIn, setInviteSignIn] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Somebody who had a box a moment ago and has none now.
  //
  // The two ways to hold no devices look identical to the server and could not
  // be less alike to the person: one has just unboxed their first Prayer Box,
  // the other has had a shared one taken away. Sending the second to the claim
  // screen asks them for a code they were never given, with Sign out as the only
  // way off it -- which reads as a broken app rather than as a thing that
  // happened.
  //
  // The list held in local storage is what tells them apart, and it is only
  // trustworthy in one direction: devices before and none now means access went
  // away. None before proves nothing, so it claims nothing.
  const [lostAccess, setLostAccess] = useState(false);

  const loadDevices = async (user) => {
    const device_url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/devices/');
    const dAuthData = {
      USER_ID: user.id,
      AUTH_KEY: user.auth_key
    };

    const serverUserDevices = await getData(device_url, dAuthData, dAuthData);
    // A user with no devices yet is a normal state, not a failure: the endpoint
    // answers 404 and getData resolves to undefined.
    const devices = Array.isArray(serverUserDevices) ? serverUserDevices : [];

    // Only ever set from a list that had something in it, so a failed request
    // cannot turn into "your access was removed".
    setLostAccess(devices.length === 0 && getSavedDevices().length > 0);

    setUserDevices(devices);
    localStorage.setItem(STORAGE_DEVICES_KEY, JSON.stringify(devices));
    return devices;
  }

  // Holding an invitation and already signed in.
  //
  // Sharing with an address that already has an account adds it directly and
  // sends no invitation, so a live one whose address has since acquired an
  // account means they signed up in between. Without this they would hold a
  // link that only ever told them to sign in, and signing in would not give
  // them the box -- a new dead end in place of the one being removed.
  useEffect(() => {
    if (!inviteToken || userInfo.id === '0') return undefined;

    let cancelled = false;
    (async () => {
      const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/auth/invite/');
      const result = await postJson(url, {
        token: inviteToken, USER_ID: userInfo.id, AUTH_KEY: userInfo.auth_key,
      });
      if (cancelled) return;

      // Spent either way. A token that has been refused does not become good by
      // being retried on every render, and the reason is on screen to act on.
      setInviteToken('');

      if (result?.ok && result.data?.ok) {
        setInviteNotice({ kind: 'info', text: result.data.message || 'The box has been added to your account.' });
        await loadDevices(userInfo);
      } else {
        setInviteNotice({
          kind: 'warn',
          text: result?.data?.error || 'That invitation could not be accepted.',
        });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken, userInfo.id]);

  const LoginSuccess = async (userData) => {

    // Set the user state
    setUserInfo(userData);
    localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(userData));

    await loadDevices(userData);
  }

  const DeviceClaimed = async () => {
    await loadDevices(userInfo);
    setAddingDevice(false);
  }

  const Logout = () => {
    localStorage.removeItem(STORAGE_USER_KEY);
    localStorage.removeItem(STORAGE_DEVICES_KEY);
    setUserInfo({ ...EMPTY_USER });
    setUserDevices([]);
    setAddingDevice(false);
    setCreatingAccount(false);
    setLostAccess(false);
    setInviteSignIn(false);
  }

  const isSignedIn = userInfo.id !== '0';
  const hasDevices = userDevices.length > 0;

  const content = () => {
    // Arriving on a reset link takes precedence over everything, including a
    // session that is still signed in: someone resetting a password may be doing
    // it precisely because that session is not theirs.
    if (resetToken) {
      return <ResetPassword token={resetToken} onDone={() => { setResetToken(''); Logout(); }} />;
    }
    if (!isSignedIn) {
      // Arriving on an invitation. It takes precedence over the sign-in form,
      // because the person holding one has very likely never signed in and the
      // form would ask them for an account they were invited to create.
      //
      // CreateAccount resolves the token itself: only the server can say who
      // invited them, which box, and whether the link is still good, and all
      // three belong on the screen that greets them.
      if (inviteToken && !inviteSignIn) {
        return (
          <CreateAccount
            inviteToken={inviteToken}
            onRegistered={async (user) => { setInviteToken(''); await LoginSuccess(user); }}
            onSignIn={() => setInviteSignIn(true)}
            onCancel={() => { setInviteToken(''); setInviteSignIn(false); }} />
        );
      }
      if (forgotPassword) {
        return <ForgotPassword onBack={() => setForgotPassword(false)} />;
      }
      if (creatingAccount) {
        // Registration signs the account in and claims the box in the same
        // request, so what comes back is a user in exactly the shape a login
        // returns -- and by the time devices load, they already own one.
        return (
          <CreateAccount
            onRegistered={async (user) => { setCreatingAccount(false); await LoginSuccess(user); }}
            onCancel={() => setCreatingAccount(false)} />
        );
      }
      return (
        <Login
          LoginSuccess={LoginSuccess}
          User={userInfo}
          onForgotPassword={() => setForgotPassword(true)}
          onCreateAccount={() => setCreatingAccount(true)} />
      );
    }
    // Told what happened, and offered both ways forward -- rather than being
    // handed a form for a code they do not have.
    if (lostAccess && !addingDevice) {
      return (
        <NoAccess
          User={userInfo}
          onAddDevice={() => setAddingDevice(true)}
          onSignOut={Logout} />
      );
    }
    if (!hasDevices || addingDevice) {
      return (
        <ClaimDevice
          User={userInfo}
          onClaimed={DeviceClaimed}
          onCancel={hasDevices ? () => setAddingDevice(false) : Logout}
          isFirstDevice={!hasDevices} />
      );
    }
    return (
      <Home
        User={userInfo}
        Devices={userDevices}
        Logout={Logout}
        onAddDevice={() => setAddingDevice(true)}
        onDevicesChanged={() => loadDevices(userInfo)} />
    );
  }

  return (
    <div className="app">
      <InstallApp />
      {inviteNotice && (
        <div className={`notice notice--${inviteNotice.kind}`} role="status">
          {inviteNotice.text}
          <button type="button" className="btn btn--sm" onClick={() => setInviteNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {content()}
      <Copyrights />
    </div>
  );
}

export default App;
